/**
 * 購物車與最新商品資料的比對邏輯（純函數，禁止引入 Vuex / API 依賴，單元測試直接 import）
 *
 * 售完語意 fail-open：僅 availableForSale === false 視為售完，
 * undefined / null（後端欄位未落地或舊快取）一律視為可售，
 * 與後端 IOptionDetail.isAvailableForSale()（CacheOptionDetail 與
 * OptionDetailResponse 共用）的 null→可售 策略一致；舊版後端無此欄位時
 * 整組售完偵測靜默不生效（相容不誤攔）。
 *
 * 回傳每筆的衝突類型清單（而非直接判生死），由呼叫端決定移除或標記。
 */

// 衝突類型（UI 紅框原因與「接受新價」判斷依據）
export const CART_CONFLICT_TYPES = {
	ITEM_REMOVED: 'ITEM_REMOVED', // 商品被刪除或下架（最新資料查無）
	ITEM_SOLD_OUT: 'ITEM_SOLD_OUT', // 頂層商品售完
	NAME_CHANGED: 'NAME_CHANGED', // 品名異動
	PRICE_CHANGED: 'PRICE_CHANGED', // 頂層價格異動（帶新舊價，可「接受新價」）
	OPTION_CHANGED: 'OPTION_CHANGED', // 口味結構 / 屬性 / 細項名價異動
	OPTION_UNAVAILABLE: 'OPTION_UNAVAILABLE', // 已選口味整組下架
	DETAIL_SOLD_OUT: 'DETAIL_SOLD_OUT', // 已選口味細項售完
	DETAIL_UNAVAILABLE: 'DETAIL_UNAVAILABLE', // 已選口味細項下架
	DETAIL_REMOVED: 'DETAIL_REMOVED', // 已選口味細項被移除
	UPGRADE_CHANGED: 'UPGRADE_CHANGED', // 升級套餐內容異動
	UPGRADE_UNAVAILABLE: 'UPGRADE_UNAVAILABLE', // 已選升級套餐下架
	COMBO_CHANGED: 'COMBO_CHANGED', // 套餐（combo）內容異動
	OPTION_QUANTITY_INVALID: 'OPTION_QUANTITY_INVALID', // 已選口味數量不符最新上下限
	CONTENT_QUANTITY_INVALID: 'CONTENT_QUANTITY_INVALID', // 套餐 / 升級群組選取數量不符最新上下限
}

// 「數量規則本身異動」是否即視為衝突。
// 預設關閉：店家把上限放寬（例如最多 3 → 5）時既有選取仍完全合法，
// 若一律標紅會造成大量無意義阻塞。開啟前需產品確認規範。
const TREAT_QUANTITY_RULE_CHANGE_AS_CONFLICT = false

function isSoldOut(target) {
	return target?.availableForSale === false
}

// 口味、口味細項與升級套餐的上下架旗標；與售完同採 fail-open（缺值視為上架）
function isUnavailable(target) {
	return target?.status === false
}

// 套餐 / 升級內層餐點的下架判斷：itemStatus 為餐點自身上下架、categoryStatus 為分類啟用狀態。
// 只看最新資料的絕對值 —— 與購物車快照比對會漏（快照本身就可能是 false）
function isItemUnavailable(serverDetail) {
	return (
		serverDetail.itemStatus === false || serverDetail.categoryStatus === false
	)
}

// 細項層級的售完 / 下架講得出是哪一項，比「套餐內容已異動」具體，套餐內保留原類型
const DETAIL_LEVEL_TYPES = [
	CART_CONFLICT_TYPES.DETAIL_SOLD_OUT,
	CART_CONFLICT_TYPES.DETAIL_UNAVAILABLE,
	// 數量不符也講得出是哪個口味與該選幾項，不應被折成籠統的「套餐內容已異動」
	CART_CONFLICT_TYPES.OPTION_QUANTITY_INVALID,
]

// 選取數量規則一律以最新資料為準；mostQuantity 為 null / 缺值代表不限上限。
// export 給 utils/optionSelection.js 的加入購物車驗證共用 —— 兩處對「不限上限」
// 的判定必須同口徑，否則會出現「詳情頁放行、購物車比對又標紅」的無解迴圈
export function getQuantityRule(target) {
	return {
		least: Number(target?.leastQuantity) || 0,
		most: target?.mostQuantity == null ? null : Number(target.mostQuantity),
	}
}

function isQuantityInvalid(count, { least, most }) {
	return count < least || (most !== null && count > most)
}

function isQuantityRuleChanged(serverTarget, cartTarget) {
	const server = getQuantityRule(serverTarget)
	const cart = getQuantityRule(cartTarget)
	return server.least !== cart.least || server.most !== cart.most
}

function formatQuantityRule(rule) {
	if (!rule) {
		return null
	}
	const { least, most } = rule
	if (most === null) {
		return `${least} 項以上`
	}
	if (least === most) {
		return `${least} 項`
	}
	return `${least}~${most} 項`
}

// 最新資料中是否還有可點的內層餐點；整組售完 / 下架時不該要求使用者去選
function hasOrderableDetail(detailList) {
	return (detailList || []).some(
		detail => !isItemUnavailable(detail) && !isSoldOut(detail),
	)
}

// 免加價細項的 price 後端型別為可空數字，null / undefined 視同 0 再比對
function isPriceChanged(serverPrice, cartPrice) {
	return (serverPrice ?? 0) !== (cartPrice ?? 0)
}

// itemDisplayState 僅「一般」可單點（2/3/4 = 僅套餐 / 升級用）；缺值 fail-open
const ITEM_DISPLAY_STATE_NORMAL = 1

function isNotDirectlyOrderable(latestItem) {
	return (
		latestItem.itemDisplayState != null &&
		latestItem.itemDisplayState !== ITEM_DISPLAY_STATE_NORMAL
	)
}

// 取出口味的已選細項 id 清單（單選 count 為字串 id、多選為字串 id 陣列）
function getSelectedDetailIds(option) {
	if (option.type) {
		return (option.count || []).map(String)
	}
	return option.count ? [String(option.count)] : []
}

/**
 * 口味清單比對（頂層口味與升級套餐內層口味共用）
 * @param {Array} cartOptionList 購物車保存的完整口味清單（含 count 選取狀態）
 * @param {Array} serverOptionList 最新商品資料的口味清單
 * @param {Object} [options]
 * @param {boolean} [options.verifyQuantity] 是否以最新規則重驗已選數量。
 *   僅頂層可開啟 —— 套餐 / 升級內層傳進來的是「口味模板」（count 恆為空，
 *   實際選取在 entry.optionSelect），在模板上驗數量會把每個必選口味都誤判為未選滿；
 *   內層數量由 collectSelectedInnerOptionConflicts 逐份重驗。
 * @returns {Array} conflicts
 */
function collectOptionConflicts(
	cartOptionList,
	serverOptionList,
	{ verifyQuantity = false } = {},
) {
	const cartList = cartOptionList || []
	const serverList = serverOptionList || []
	const conflicts = []

	// 店家新掛載且顯示中的「必選」口味，購物車沒有 → 異動。
	// 新增選填口味不影響既有選取的合法性（不選也能成單），一律標紅只會製造
	// 無從處理的假陽性；required 缺值時比照選填放行，與其他欄位的 fail-open 一致。
	serverList.forEach(serverOption => {
		if (serverOption.status === false || serverOption.required !== true) {
			return
		}
		if (
			!cartList.some(
				cartOption => String(cartOption.id) === String(serverOption.id),
			)
		) {
			conflicts.push({
				type: CART_CONFLICT_TYPES.OPTION_CHANGED,
				optionName: serverOption.name,
			})
		}
	})

	cartList.forEach(cartOption => {
		const serverOption = serverList.find(
			o => String(o.id) === String(cartOption.id),
		)
		const selectedIds = getSelectedDetailIds(cartOption)

		// 口味整組被移除：僅在購物車有選取時視為異動
		if (!serverOption) {
			if (selectedIds.length > 0) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_CHANGED,
					optionName: cartOption.name,
				})
			}
			return
		}

		// 口味整組被下架：仍留在最新資料中（status false），僅在購物車有選取時視為異動
		if (isUnavailable(serverOption)) {
			if (selectedIds.length > 0) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_UNAVAILABLE,
					optionName: cartOption.name,
				})
			}
			return
		}

		if (
			serverOption.name !== cartOption.name ||
			serverOption.required !== cartOption.required ||
			serverOption.type !== cartOption.type ||
			(TREAT_QUANTITY_RULE_CHANGE_AS_CONFLICT &&
				isQuantityRuleChanged(serverOption, cartOption))
		) {
			conflicts.push({
				type: CART_CONFLICT_TYPES.OPTION_CHANGED,
				optionName: cartOption.name,
			})
			return
		}

		const conflictCountBeforeDetails = conflicts.length

		// 已選細項逐一比對：被移除、售完、名稱或價格異動皆視為不一致
		selectedIds.forEach(selectedId => {
			const serverDetail = serverOption.optionDetailList?.find(
				detail => String(detail.id) === selectedId,
			)
			const cartDetail = cartOption.optionDetailList?.find(
				detail => String(detail.id) === selectedId,
			)

			if (!serverDetail) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_REMOVED,
					optionName: cartOption.name,
					detailName: cartDetail?.name,
				})
				return
			}
			if (isUnavailable(serverDetail)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_UNAVAILABLE,
					optionName: cartOption.name,
					detailName: serverDetail.name,
				})
				return
			}
			if (isSoldOut(serverDetail)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_SOLD_OUT,
					optionName: cartOption.name,
					detailName: serverDetail.name,
				})
				return
			}
			if (
				!cartDetail ||
				serverDetail.name !== cartDetail.name ||
				isPriceChanged(serverDetail.price, cartDetail.price)
			) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_CHANGED,
					optionName: cartOption.name,
					detailName: serverDetail.name,
					// 帶上新舊值讓訊息講得出「改成什麼」；購物車端查無該細項時無舊值可比，
					// 略過這些欄位、由 formatConflictMessage 走通用文案
					...(cartDetail && {
						oldName: cartDetail.name,
						newName: serverDetail.name,
						oldPrice: cartDetail.price ?? 0,
						newPrice: serverDetail.price ?? 0,
					}),
				})
			}
		})

		// 以最新規則重驗既有選取數量（單選由 required 覆蓋，不重複判斷）。
		// 細項層已報出更具體的移除 / 售完 / 名價異動時不再疊加數量訊息。
		if (
			verifyQuantity &&
			serverOption.type &&
			conflicts.length === conflictCountBeforeDetails
		) {
			const rule = getQuantityRule(serverOption)
			if (isQuantityInvalid(selectedIds.length, rule)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_QUANTITY_INVALID,
					optionName: cartOption.name,
					rule,
				})
			}
		}
	})

	return conflicts
}

/**
 * 內層口味「實際選取」比對（combo / upgrade 細項用）
 *
 * 購物車細項的 optionList 只是口味模板（count 恆為空），實際選取存於
 * entry.optionSelect —— 每份餐一個陣列，元素形如
 * { ...口味選項, optionDetailSelect: 選取當下的細項物件 }，
 * 與 utils/order.js 的計價、送單同源。
 * @param {Array} optionSelect 每份餐的選取清單
 * @param {Array} cartOptionList 購物車細項保存的口味模板
 * @param {Array} serverOptionList 最新細項的口味清單
 * @returns {Array} conflicts
 */
function collectSelectedInnerOptionConflicts(
	optionSelect,
	cartOptionList,
	serverOptionList,
) {
	const conflicts = []
	;(optionSelect || []).forEach(selections => {
		// 每份餐先為最新且仍上架的複選口味建立 0 計數；若只在有合法選取時
		// 才建立項目，零選取永遠不會進入下方的最新下限驗證。只納入購物車
		// 已有且結構相符的群組；新增／結構異動仍交由 collectOptionConflicts 判斷。
		const validCountByOptionId = new Map(
			(serverOptionList || [])
				.filter(serverOption => {
					const cartOption = (cartOptionList || []).find(
						option => String(option.id) === String(serverOption.id),
					)
					return (
						cartOption &&
						serverOption?.type &&
						!isUnavailable(serverOption) &&
						serverOption.name === cartOption.name &&
						serverOption.required === cartOption.required &&
						serverOption.type === cartOption.type
					)
				})
				.map(option => [
					String(option.id),
					{
						count: 0,
						serverOption: option,
						optionName: option.name,
					},
				]),
		)
		const optionIdsWithSpecificConflict = new Set()

		;(selections || []).forEach(selection => {
			const cartDetail = selection?.optionDetailSelect
			if (!cartDetail) {
				return
			}
			const serverOption = (serverOptionList || []).find(
				option => String(option.id) === String(selection.id),
			)
			if (!serverOption) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_CHANGED,
					optionName: selection.name,
				})
				return
			}
			if (isUnavailable(serverOption)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_UNAVAILABLE,
					optionName: selection.name,
				})
				return
			}
			const key = String(serverOption.id)
			const serverDetail = serverOption.optionDetailList?.find(
				detail => String(detail.id) === String(cartDetail.id),
			)
			if (!serverDetail) {
				optionIdsWithSpecificConflict.add(key)
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_REMOVED,
					optionName: selection.name,
					detailName: cartDetail.name,
				})
				return
			}
			if (isUnavailable(serverDetail)) {
				optionIdsWithSpecificConflict.add(key)
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_UNAVAILABLE,
					optionName: selection.name,
					detailName: serverDetail.name,
				})
				return
			}
			if (isSoldOut(serverDetail)) {
				optionIdsWithSpecificConflict.add(key)
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_SOLD_OUT,
					optionName: selection.name,
					detailName: serverDetail.name,
				})
				return
			}
			if (
				serverDetail.name !== cartDetail.name ||
				isPriceChanged(serverDetail.price, cartDetail.price)
			) {
				optionIdsWithSpecificConflict.add(key)
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_CHANGED,
					optionName: selection.name,
					detailName: serverDetail.name,
				})
				return
			}

			// 這筆選取完全合法才計入數量，避免售完 / 已移除的項目同時被算進數量又被報兩次
			const accumulated = validCountByOptionId.get(key)
			validCountByOptionId.set(key, {
				count: (accumulated?.count || 0) + 1,
				serverOption,
				optionName: selection.name,
			})
		})

		// 每份餐獨立以最新規則重驗（單選由 required 覆蓋，不重複判斷）
		validCountByOptionId.forEach(({ count, serverOption, optionName }) => {
			if (!serverOption?.type) {
				return
			}
			// 修正前若唯一已選細項已售完／移除，map 不會建立，因此只報具體衝突；
			// 預建 0 計數後仍須保留此行為，避免同時再報一筆數量不足。
			if (
				count === 0 &&
				optionIdsWithSpecificConflict.has(String(serverOption.id))
			) {
				return
			}
			const rule = getQuantityRule(serverOption)
			if (isQuantityInvalid(count, rule)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.OPTION_QUANTITY_INVALID,
					optionName,
					rule,
				})
			}
		})
	})
	return conflicts
}

// 升級套餐比對（含套餐細項與其內層口味）
function collectUpgradeConflicts(cartItem, latestItem) {
	const upgradeListId = cartItem.upgradeListId
	if (!upgradeListId || upgradeListId === 'null') {
		return []
	}

	const serverUpgrade = (latestItem.upgradeList || []).find(
		upgrade => String(upgrade.upgradeId) === String(upgradeListId),
	)
	if (!serverUpgrade) {
		return [{ type: CART_CONFLICT_TYPES.UPGRADE_CHANGED }]
	}
	// 升級套餐被下架：仍留在最新資料中（status false）
	if (isUnavailable(serverUpgrade)) {
		return [{ type: CART_CONFLICT_TYPES.UPGRADE_UNAVAILABLE }]
	}
	if (
		serverUpgrade.name !== cartItem.upgradeName ||
		isPriceChanged(serverUpgrade.price, cartItem.upgradePrice)
	) {
		return [{ type: CART_CONFLICT_TYPES.UPGRADE_CHANGED }]
	}

	const conflicts = []

	// 店家新掛載的必選群組：購物車的 upgradeSelect 沒有它，正向迭代看不見。
	// 只在「最新規則要求至少選 1 項」且「確實有可點的餐點」時才算衝突。
	;(serverUpgrade.upgradeContentList || []).forEach(serverContent => {
		const inCart = (cartItem.upgradeSelect || []).some(
			cartContent =>
				String(cartContent.upgradeContentId) ===
				String(serverContent.upgradeContentId),
		)
		if (inCart) {
			return
		}
		if (
			getQuantityRule(serverContent).least > 0 &&
			hasOrderableDetail(serverContent.upgradeContentDetailList)
		) {
			conflicts.push({ type: CART_CONFLICT_TYPES.UPGRADE_CHANGED })
		}
	})
	;(cartItem.upgradeSelect || []).forEach(cartContent => {
		const serverContent = serverUpgrade.upgradeContentList?.find(
			content =>
				String(content.upgradeContentId) ===
				String(cartContent.upgradeContentId),
		)
		if (!serverContent || serverContent.name !== cartContent.name) {
			conflicts.push({ type: CART_CONFLICT_TYPES.UPGRADE_CHANGED })
			return
		}

		// 群組選取份數以最新上下限重驗（數量取各細項 count 加總）
		const selectedCount = (cartContent.upgradeContentDetails || []).reduce(
			(sum, entry) => sum + (Number(entry.upgradeContentDetail?.count) || 0),
			0,
		)
		const contentRule = getQuantityRule(serverContent)
		if (isQuantityInvalid(selectedCount, contentRule)) {
			conflicts.push({
				type: CART_CONFLICT_TYPES.CONTENT_QUANTITY_INVALID,
				contentName: serverContent.name,
				rule: contentRule,
			})
		}

		;(cartContent.upgradeContentDetails || []).forEach(entry => {
			const cartDetail = entry.upgradeContentDetail
			if (!cartDetail) {
				conflicts.push({ type: CART_CONFLICT_TYPES.UPGRADE_CHANGED })
				return
			}

			const serverDetail = serverContent.upgradeContentDetailList?.find(
				detail =>
					String(detail.upgradeContentDetailId) ===
					String(cartDetail.upgradeContentDetailId),
			)
			if (!serverDetail) {
				conflicts.push({ type: CART_CONFLICT_TYPES.UPGRADE_CHANGED })
				return
			}
			if (isItemUnavailable(serverDetail)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_UNAVAILABLE,
					detailName: serverDetail.itemName,
				})
				return
			}
			if (isSoldOut(serverDetail)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_SOLD_OUT,
					detailName: serverDetail.itemName,
				})
				return
			}
			if (
				serverDetail.categoryId !== cartDetail.categoryId ||
				serverDetail.itemId !== cartDetail.itemId ||
				serverDetail.itemName !== cartDetail.itemName ||
				isPriceChanged(serverDetail.price, cartDetail.price)
			) {
				conflicts.push({ type: CART_CONFLICT_TYPES.UPGRADE_CHANGED })
				return
			}

			// 模板結構比對（新掛載 / 口味屬性異動）+ 實際選取比對（售完 / 移除 / 名價異動）
			;[
				...collectOptionConflicts(
					cartDetail.optionList,
					serverDetail.optionList,
				),
				...collectSelectedInnerOptionConflicts(
					entry.optionSelect,
					cartDetail.optionList,
					serverDetail.optionList,
				),
			].forEach(conflict =>
				conflicts.push(
					DETAIL_LEVEL_TYPES.includes(conflict.type)
						? conflict
						: { type: CART_CONFLICT_TYPES.UPGRADE_CHANGED },
				),
			)
		})
	})
	return conflicts
}

// 套餐 combo 比對（已選 content / detail 與其內層口味；未選取的 detail 不比對）
function collectComboConflicts(cartItem, latestItem) {
	const conflicts = []

	// 店家新掛載的必選群組：購物車的 comboSelect 沒有它，正向迭代看不見
	;(latestItem.comboList || []).forEach(serverContent => {
		const inCart = (cartItem.comboSelect || []).some(
			cartContent => String(cartContent.id) === String(serverContent.id),
		)
		if (inCart) {
			return
		}
		if (
			getQuantityRule(serverContent).least > 0 &&
			hasOrderableDetail(serverContent.detailList)
		) {
			conflicts.push({ type: CART_CONFLICT_TYPES.COMBO_CHANGED })
		}
	})
	;(cartItem.comboSelect || []).forEach(cartContent => {
		const serverContent = (latestItem.comboList || []).find(
			content => String(content.id) === String(cartContent.id),
		)
		if (!serverContent || serverContent.name !== cartContent.name) {
			conflicts.push({ type: CART_CONFLICT_TYPES.COMBO_CHANGED })
			return
		}

		// 群組選取份數以最新上下限重驗（數量取各細項 count 加總）
		const selectedCount = (cartContent.details || []).reduce(
			(sum, entry) => sum + (Number(entry.detail?.count) || 0),
			0,
		)
		const contentRule = getQuantityRule(serverContent)
		if (isQuantityInvalid(selectedCount, contentRule)) {
			conflicts.push({
				type: CART_CONFLICT_TYPES.CONTENT_QUANTITY_INVALID,
				contentName: serverContent.name,
				rule: contentRule,
			})
		}
		;(cartContent.details || []).forEach(entry => {
			const cartDetail = entry.detail
			if (!cartDetail) {
				conflicts.push({ type: CART_CONFLICT_TYPES.COMBO_CHANGED })
				return
			}
			const serverDetail = serverContent.detailList?.find(
				detail => String(detail.id) === String(cartDetail.id),
			)
			if (!serverDetail) {
				conflicts.push({ type: CART_CONFLICT_TYPES.COMBO_CHANGED })
				return
			}
			if (isItemUnavailable(serverDetail)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_UNAVAILABLE,
					detailName: serverDetail.itemName,
				})
				return
			}
			if (isSoldOut(serverDetail)) {
				conflicts.push({
					type: CART_CONFLICT_TYPES.DETAIL_SOLD_OUT,
					detailName: serverDetail.itemName,
				})
				return
			}
			if (
				serverDetail.categoryId !== cartDetail.categoryId ||
				serverDetail.itemId !== cartDetail.itemId ||
				serverDetail.itemName !== cartDetail.itemName ||
				isPriceChanged(serverDetail.price, cartDetail.price)
			) {
				conflicts.push({ type: CART_CONFLICT_TYPES.COMBO_CHANGED })
				return
			}
			// 模板結構比對（新掛載 / 口味屬性異動）+ 實際選取比對（售完 / 移除 / 名價異動）
			;[
				...collectOptionConflicts(
					cartDetail.optionList,
					serverDetail.optionList,
				),
				...collectSelectedInnerOptionConflicts(
					entry.optionSelect,
					cartDetail.optionList,
					serverDetail.optionList,
				),
			].forEach(conflict =>
				conflicts.push(
					DETAIL_LEVEL_TYPES.includes(conflict.type)
						? conflict
						: { type: CART_CONFLICT_TYPES.COMBO_CHANGED },
				),
			)
		})
	})
	return conflicts
}

function collectItemConflicts(cartItem, latestItem) {
	// 最新資料查無（或髒資料無品名）→ 視為商品被刪除
	if (latestItem?.itemName == null) {
		return [{ type: CART_CONFLICT_TYPES.ITEM_REMOVED }]
	}
	// getItems 不過濾下架商品（與 getMenu 不同），需自行檢查：
	// itemStatus 僅代表餐點自身上下架、categoryStatus 為分類啟用狀態，
	// itemDisplayState 非「一般」代表僅供套餐 / 升級選用、不可單點，
	// 皆採 fail-open —— 明確不可售才視為下架。
	// 注意 itemDisplayState 僅適用頂層商品，套餐 / 升級內層細項本就非「一般」
	if (
		latestItem.itemStatus === false ||
		latestItem.categoryStatus === false ||
		isNotDirectlyOrderable(latestItem)
	) {
		return [{ type: CART_CONFLICT_TYPES.ITEM_REMOVED }]
	}

	const conflicts = []
	if (latestItem.itemName !== cartItem.itemName) {
		conflicts.push({
			type: CART_CONFLICT_TYPES.NAME_CHANGED,
			oldName: cartItem.itemName,
			newName: latestItem.itemName,
		})
	}
	if (isPriceChanged(latestItem.price, cartItem.itemPrice)) {
		conflicts.push({
			type: CART_CONFLICT_TYPES.PRICE_CHANGED,
			oldPrice: cartItem.itemPrice,
			newPrice: latestItem.price,
		})
	}
	if (isSoldOut(latestItem)) {
		conflicts.push({ type: CART_CONFLICT_TYPES.ITEM_SOLD_OUT })
	}
	conflicts.push(
		...collectOptionConflicts(cartItem.optionList, latestItem.optionList, {
			// 頂層 optionList 帶有真實選取狀態，可直接以最新規則重驗數量
			verifyQuantity: true,
		}),
	)
	conflicts.push(...collectUpgradeConflicts(cartItem, latestItem))
	conflicts.push(...collectComboConflicts(cartItem, latestItem))
	return conflicts
}

// 衝突原因轉顯示文字（UI 紅框用）
export function formatConflictMessage(conflict) {
	const {
		type,
		detailName,
		optionName,
		oldName,
		newName,
		oldPrice,
		newPrice,
		contentName,
		rule,
	} = conflict
	switch (type) {
		case CART_CONFLICT_TYPES.ITEM_REMOVED:
			return '商品已下架'
		case CART_CONFLICT_TYPES.ITEM_SOLD_OUT:
			return '商品已售完'
		case CART_CONFLICT_TYPES.NAME_CHANGED:
			return `品名已改為「${newName}」`
		case CART_CONFLICT_TYPES.PRICE_CHANGED:
			return `價格 $${oldPrice} → $${newPrice}`
		case CART_CONFLICT_TYPES.DETAIL_SOLD_OUT:
			return `「${detailName}」已售完`
		case CART_CONFLICT_TYPES.OPTION_UNAVAILABLE:
			return optionName ? `口味「${optionName}」已下架` : '已選口味已下架'
		// 來源含口味細項與套餐 / 升級內層餐點，缺名時的通用文案不能寫死「口味」
		case CART_CONFLICT_TYPES.DETAIL_UNAVAILABLE:
			return detailName ? `「${detailName}」已下架` : '已選項目已下架'
		case CART_CONFLICT_TYPES.DETAIL_REMOVED:
			return detailName ? `「${detailName}」已被移除` : '已選口味已被移除'
		case CART_CONFLICT_TYPES.OPTION_CHANGED:
			// 只有「已選細項的名 / 價異動」帶得出新舊值；口味新掛載、整組移除、
			// 屬性（必選 / 單複選）變更等來源沒有可比的舊值，一律走通用文案
			if (detailName && newPrice != null && oldPrice !== newPrice) {
				return `口味「${optionName}」的「${detailName}」$${oldPrice} → $${newPrice}`
			}
			if (oldName && newName && oldName !== newName) {
				return `口味「${optionName}」的「${oldName}」已改名為「${newName}」`
			}
			return optionName ? `口味「${optionName}」內容已異動` : '口味內容已異動'
		// conflicts 會隨 user 模組持久化，舊資料還原時可能沒有 rule → 走通用文案
		case CART_CONFLICT_TYPES.OPTION_QUANTITY_INVALID: {
			const ruleText = formatQuantityRule(rule)
			const target = optionName ? `口味「${optionName}」` : '口味'
			return ruleText
				? `${target}需選 ${ruleText}，請重新選擇`
				: `${target}的可選數量已異動，請重新選擇`
		}
		case CART_CONFLICT_TYPES.CONTENT_QUANTITY_INVALID: {
			const ruleText = formatQuantityRule(rule)
			const target = contentName ? `「${contentName}」` : '套餐內容'
			return ruleText
				? `${target}需選 ${ruleText}，請重新選擇`
				: `${target}的可選數量已異動，請重新選擇`
		}
		case CART_CONFLICT_TYPES.UPGRADE_UNAVAILABLE:
			return '升級套餐已下架'
		case CART_CONFLICT_TYPES.UPGRADE_CHANGED:
		case CART_CONFLICT_TYPES.COMBO_CHANGED:
			return '套餐內容已異動'
		default:
			return '餐點內容已異動'
	}
}

// 是否可一鍵「接受新價」保留（衝突僅有頂層價格異動時）
export function canAcceptNewPrice(conflicts) {
	return (
		conflicts?.length === 1 &&
		conflicts[0].type === CART_CONFLICT_TYPES.PRICE_CHANGED
	)
}

/**
 * @param {Array} storeCart 當前店家的購物車項目陣列
 * @param {Object} latestItemsById getItems 結果，以 itemId 為 key
 * @returns {{ results: Array<{item: Object, conflicts: Array}>, conflictItemNames: Array<string>, hasConflicts: boolean }}
 */
export function compareCartWithLatest(storeCart, latestItemsById) {
	const conflictItemNames = new Set()
	const latestMap = latestItemsById || {}

	const results = (storeCart || []).map(cartItem => {
		const conflicts = collectItemConflicts(cartItem, latestMap[cartItem.itemId])
		if (conflicts.length > 0) {
			conflictItemNames.add(cartItem.itemName)
		}
		return { item: cartItem, conflicts }
	})

	return {
		results,
		conflictItemNames: [...conflictItemNames],
		hasConflicts: conflictItemNames.size > 0,
	}
}
